import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { PageHeader, StatusPill, type StatusTone } from "@ozpanel/kit";
import { Button, Panel, Section, StateView, formatRelative, useAuth } from "@ozpanel/ui";

// OpenAlertsScreen — what is wrong NOW, on every node, and why.
//
// The bell is a history (what was raised, read or not); the alert levels are configuration. Neither
// answered "what is breached right now". GET /ozpanel/alerts/open answers it from the mechanisms that
// decide — each node's alert engine (open breaches), its alerts' failing reads (they cannot fire while
// that lasts: an open problem, never "quiet") and its last module-health reading — and asks every other
// node for its own answer over the node-to-node channel.
//
// That fan-out costs one call per node, so it runs when this screen is opened or "Read again" is
// pressed — never on an interval or a bell render. A node that could not be asked is UNKNOWN, never
// "nothing open".

/** One open item — server.openAlertsHere's JSON, field for field. */
interface OpenItem {
  kind: "breach" | "unmeasured" | "health" | string;
  module: string;
  module_title?: string;
  key?: string;
  label?: string;
  instance?: string;
  because?: string;
  severity?: string;
  unit?: string;
  value?: number;
  at?: string;
  threshold?: number;
  deviation_pct?: number;
  average?: number;
  since?: string;
  category?: string;
  /** unmeasured: the read's own error; health: why it could not be measured */
  detail?: string;
  failing_reads?: number;
  raised?: boolean;
  /** health: the module's status and failing checks */
  status?: string;
  measured_at?: string;
  checks?: { name: string; detail?: string; unknown?: boolean }[] | null;
}

/** One node's own answer (the peer verb's payload). */
interface NodeReport {
  node: string;
  items: OpenItem[] | null;
  total: number;
  counts?: Record<string, number>;
  health_not_read?: string[] | null;
  health_watched?: number;
  alerts_collected?: boolean;
  at?: string;
}

interface NodeOpen {
  node: string;
  self: boolean;
  /** false: the node could not be asked — UNKNOWN, not zero. */
  measured: boolean;
  error?: string;
  took_ms: number;
  report?: NodeReport;
}

interface OpenAlerts {
  node: string;
  scope: string;
  nodes: NodeOpen[] | null;
  total: number;
  unknown: string[] | null;
  registry_error?: string;
  at?: string;
  note?: string;
}

const sevTone = (s?: string): StatusTone => (s === "crit" || s === "error" ? "bad" : s === "warn" ? "warn" : "neutral");

const KIND_LABEL: Record<string, string> = {
  breach: "breached",
  unmeasured: "cannot be measured",
  health: "health",
};

function level(it: OpenItem): string | null {
  if (it.threshold) return `level ${it.threshold}${it.unit ? " " + it.unit : ""}`;
  if (it.deviation_pct) return `${it.deviation_pct}% over its average${it.average != null ? ` (${it.average.toFixed(2)})` : ""}`;
  return null;
}

function Row({ it }: { it: OpenItem }) {
  const title = it.kind === "health" ? `${it.module_title ?? it.module} is ${it.status}` : it.label ?? it.key ?? "alert";
  return (
    <div className="px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <StatusPill tone={sevTone(it.severity)}>{it.severity ?? "unknown"}</StatusPill>
        <StatusPill tone={it.kind === "breach" ? "bad" : "warn"}>{KIND_LABEL[it.kind] ?? it.kind}</StatusPill>
        <span className="text-[13.5px] font-medium">{title}</span>
        {it.instance && <span className="font-mono text-[11.5px] text-muted">{it.instance}</span>}
        <Link to={`/modules/${it.module}`} className="text-[11.5px] text-accent hover:underline">
          {it.module_title ?? it.module}
        </Link>
        <span className="ml-auto text-[11.5px] text-muted">
          {it.since
            ? `since ${formatRelative(it.since)}`
            : it.kind === "health"
              ? `last read ${formatRelative(it.measured_at)} · since: not tracked`
              : "since: unknown"}
        </span>
      </div>
      {it.kind === "breach" && (
        <div className="mt-1 font-mono text-[12px]">
          now {it.value ?? "—"}
          {it.unit ? ` ${it.unit}` : ""}
          {level(it) && <span className="text-muted"> · {level(it)}</span>}
        </div>
      )}
      {it.kind === "unmeasured" && (
        <div className="mt-1 text-[12px] leading-relaxed">
          <span className="font-mono text-warn">{it.detail || "the read fails and says nothing"}</span>
          <span className="text-muted">
            {" "}— {it.failing_reads ?? "?"} failed read(s). While this lasts the alert cannot fire
            {it.raised ? "; this was announced as an alert." : "."}
          </span>
        </div>
      )}
      {it.kind === "health" && (
        <div className="mt-1 space-y-0.5 text-[12px] leading-relaxed text-muted">
          {it.detail && <div>{it.detail}</div>}
          {(it.checks ?? []).map((c) => (
            <div key={c.name}>
              <span className="font-mono text-fg">{c.name}</span>
              {c.unknown && <StatusPill tone="warn">not measured</StatusPill>}
              {c.detail ? ` — ${c.detail}` : ""}
            </div>
          ))}
        </div>
      )}
      {it.because && <div className="mt-1 text-[12px] leading-relaxed text-muted">{it.because}</div>}
    </div>
  );
}

/** The node's own tone: unknown is a warning, never ok. */
function nodeTone(n: NodeOpen): StatusTone {
  if (!n.measured) return "warn";
  const items = n.report?.items ?? [];
  if (items.some((i) => sevTone(i.severity) === "bad")) return "bad";
  return items.length || (n.report?.health_not_read ?? []).length ? "warn" : "ok";
}

function NodeSection({ n }: { n: NodeOpen }) {
  const items = n.report?.items ?? [];
  const notRead = n.report?.health_not_read ?? [];
  const status = !n.measured
    ? "unknown"
    : items.length
      ? `${items.length} open`
      : notRead.length
        ? "partly unknown"
        : "nothing open";
  return (
    <Section
      title={`${n.node}${n.self ? " (this node)" : ""}`}
      hint={<StatusPill tone={nodeTone(n)}>{status}</StatusPill>}
      bodyClassName="p-0"
    >
      {!n.measured ? (
        <div className="px-4 py-4 text-[12.5px] leading-relaxed">
          <span className="text-warn">Could not be asked, so its open alerts are UNKNOWN — not "nothing open".</span>
          {n.error && <span className="ml-1 font-mono text-[11.5px] text-muted">{n.error}</span>}
        </div>
      ) : (
        <>
          {n.report?.alerts_collected === false && (
            <Panel className="m-3 p-3 text-[12.5px] text-warn">
              Alert collection is not wired on this node, so no measured alert is known here — only module health.
            </Panel>
          )}
          {items.length === 0 ? (
            <div className="px-4 py-4 text-[12.5px] text-muted">
              Nothing is breached, unmeasurable or unhealthy
              {notRead.length ? " among what has been read." : "."}
            </div>
          ) : (
            <div className="divide-y divide-line">
              {items.map((it, i) => (
                <Row key={`${it.kind}.${it.module}.${it.key ?? ""}.${it.instance ?? ""}.${i}`} it={it} />
              ))}
            </div>
          )}
          {notRead.length > 0 && (
            <p className="border-t border-line px-4 py-2.5 text-[12px] leading-relaxed text-muted">
              Not read yet, so unknown here — not fine: {notRead.join(", ")}.
            </p>
          )}
          <p className="border-t border-line px-4 py-2 text-[11px] text-faint">
            answered {n.report?.at ? formatRelative(n.report.at) : "—"} · {n.took_ms} ms
          </p>
        </>
      )}
    </Section>
  );
}

export function OpenAlertsScreen() {
  const { client } = useAuth();
  const q = useQuery({
    queryKey: ["alerts-open"],
    queryFn: () => client.apiFetch<OpenAlerts>("/ozpanel/alerts/open"),
    // Every view asks every node: no interval, no refetch on focus. "Read again" asks again.
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });
  const d = q.data;
  const nodes = d?.nodes ?? [];
  const unknown = d?.unknown ?? [];
  const tone: StatusTone = nodes.some((n) => nodeTone(n) === "bad") ? "bad" : nodes.some((n) => nodeTone(n) === "warn") ? "warn" : "ok";

  return (
    <div>
      <PageHeader
        crumbs={[{ label: "Notifications", to: "/notifications" }, { label: "Open alerts" }]}
        title="Open alerts"
        status={
          d ? (
            <StatusPill tone={tone}>
              {d.total ? `${d.total} open` : unknown.length ? "partly unknown" : "nothing open"}
              {unknown.length ? ` · ${unknown.length} node(s) unknown` : ""}
            </StatusPill>
          ) : undefined
        }
        subtitle={
          d
            ? `What is breached, unmeasurable or unhealthy now — ${d.scope}, each node answering for itself.`
            : "What is breached, unmeasurable or unhealthy now, on every node."
        }
        actions={
          <Button size="sm" variant="ghost" disabled={q.isFetching} onClick={() => void q.refetch()}>
            {q.isFetching ? "Asking every node…" : "Read again"}
          </Button>
        }
      />
      <StateView loading={q.isLoading} error={q.error} errorContext="the open alerts">
        {d && (
          <div className="space-y-4">
            {d.registry_error && <Panel className="p-3 text-[12.5px] text-warn">{d.registry_error}</Panel>}
            {nodes.map((n) => (
              <NodeSection key={n.node} n={n} />
            ))}
            {d.note && <p className="text-[11.5px] leading-relaxed text-faint">{d.note}</p>}
          </div>
        )}
      </StateView>
    </div>
  );
}
